/* ==========================================================================
   Wild Brew — accessibility patches for third-party widget markup
   --------------------------------------------------------------------------
   Judge.me renders its own HTML, so we can't fix these at source. This script
   repairs the ARIA problems after the widget mounts, and re-runs when the
   widget re-renders (sorting, "Load More", pagination).

   INSTALL
   1. Shopify admin → Online Store → Themes → ⋯ → Edit code
   2. Assets → Add a new asset → Upload file → wildbrew-a11y.js
   3. layout/theme.liquid, immediately before </body>:
        <script src="{{ 'wildbrew-a11y.js' | asset_url }}" defer></script>
   ========================================================================== */
(function () {
  'use strict';

  var FLAG = 'wbA11y';

  function done(el) { return el.dataset[FLAG] === '1'; }
  function mark(el) { el.dataset[FLAG] = '1'; }

  /* ------------------------------------------------------------------------
     FIX 1 — "Elements use prohibited ARIA attributes"

     Judge.me puts aria-label on bare <i> elements. An element with no role
     has an implicit "generic" role, and generic elements are forbidden from
     having an accessible name — so the label is thrown away and axe flags it.

     We give them role="button", which makes the label legal. But once we've
     told assistive tech these are buttons, they have to actually behave like
     buttons, so we add keyboard activation too. Half-fixing this (role with
     no keyboard support) trades one failure for a worse real-world problem.
     ---------------------------------------------------------------------- */
  function fixProhibitedAria() {
    var sel = '.jdgm-rev-snippet-widget__prev-btn, .jdgm-rev-snippet-widget__next-btn';

    document.querySelectorAll(sel).forEach(function (el) {
      if (done(el)) return;

      el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');

      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          el.click();
        }
      });

      mark(el);
    });
  }

  /* ------------------------------------------------------------------------
     FIX 2 — "Links do not have a discernible name"

     The review snippet cards are <a role="button"> wrapping only decorative
     content, so they expose no accessible name at all. We label each one with
     the reviewer's name where we can find it, and fall back to a number.
     ---------------------------------------------------------------------- */
  function fixReviewCardLinks() {
    document.querySelectorAll('.jdgm-rev-snippet-card__full-review-link').forEach(function (el, i) {
      if (done(el)) return;

      // Already has a name from somewhere? Leave it alone.
      if (el.textContent.trim() || el.getAttribute('aria-label')) { mark(el); return; }

      var card = el.closest('.jdgm-rev-snippet-card');
      var author = card && card.querySelector('.jdgm-rev-snippet-card__author, .jdgm-rev__author');
      var name = author ? author.textContent.trim() : '';

      el.setAttribute('aria-label', name
        ? 'Read the full review by ' + name
        : 'Read full review ' + (i + 1));

      mark(el);
    });
  }

  /* ------------------------------------------------------------------------
     FIX 3 — "Identical links have the same purpose"

     Every customer photo link is labelled "Link to user picture 1". Lighthouse
     leaves this unscored, but it makes the photo gallery genuinely unusable
     with a screen reader — every link announces the same thing.

     No `done()` guard here: when new reviews load the numbering has to be
     recalculated across the whole set.
     ---------------------------------------------------------------------- */
  function fixReviewPhotoLinks() {
    document.querySelectorAll('.jdgm-rev__pic-link').forEach(function (el, i) {
      var review = el.closest('.jdgm-rev');
      var author = review && review.querySelector('.jdgm-rev__author');
      var name = author ? author.textContent.trim() : '';

      el.setAttribute('aria-label', name
        ? 'Photo from the review by ' + name
        : 'Customer photo ' + (i + 1));
    });
  }

  function run() {
    try {
      fixProhibitedAria();
      fixReviewCardLinks();
      fixReviewPhotoLinks();
    } catch (err) {
      // Never let an accessibility patch break the page.
      console.warn('[wb-a11y]', err);
    }
  }

  /* ------------------------------------------------------------------------
     Judge.me mounts asynchronously and re-renders on user interaction, so a
     single pass at DOMContentLoaded isn't enough. The observer is debounced
     to one pass per animation frame so it stays off the critical path.
     ---------------------------------------------------------------------- */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; run(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true
  });
})();